import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { sanitizeUserName, UserService } from "../utils/users";
import { getCurrentVehicle } from "../utils/device";
import { SubscriptionsService } from "../services/base";
import Api from "./api";
import { DriveStore } from "../store/reducers";
import { observeStore } from "../utils/store";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { FeatureFlagName } from "../utils/featureFlagName";
import { NODEVICEID, NOUSERID } from "../model/DutyStatusLog";
import { isInCanada } from "hos-tools";
import { getServerDate, shiftDateTime } from "../utils/storage/date";
import { IGpsData, SpeedSource } from "../store/mobile/types";
import { getLastPosition } from "../utils/location";
import { log, error, Source } from "../utils/logger";
import HosProcess from "./hosProcess";
import User from "../model/User";
import { getDeviceStatusInfo, getStatusData } from "../utils/helpers";
import { MobileActions } from "../store/mobile/actions";
import { dateTimeComparator } from "../utils/date";
import VehicleProximity from "./vehicleProximity";
import constants, { MINUTES, SECONDS } from "../constants";
import { IFeatureFlagsService } from "./featureFlags";

/**
 * Service is responsible for handling the lockscreen
 * the service does not update data.driving state, but fetch datas and listen to driving state changes
 */
export default class ScreenHandler extends SubscriptionsService {
    private gpsData?: IGpsData;
    private gpsDataCallback?: (data: IGpsData) => void;

    constructor(
        private readonly api: Api,
        private readonly userService: UserService,
        private readonly vehicleProximityService: VehicleProximity,
        private readonly store: DriveStore,
        private readonly hosProcess: HosProcess,
        private readonly featureFlagsService: IFeatureFlagsService
    ) {
        super();
    }

    startSubscriptions() {
        this.stopSubscriptions();
        this.storeUnsubscribe = observeStore(this.store, state => state.data.driving, () => this.drivingStatusChanged());

        this.gpsDataCallback = (data: IGpsData) => {
            this.gpsData = data;
        };
        dispatcher.on(DispatcherEvents.NewPositionDataForLockScreen, this.gpsDataCallback);
    }

    stopSubscriptions() {
        this.storeUnsubscribe && this.storeUnsubscribe();

        if (this.gpsDataCallback) {
            dispatcher.off(DispatcherEvents.NewPositionDataForLockScreen, this.gpsDataCallback);
            this.gpsDataCallback = undefined;
        }
        this.gpsData = undefined;
    }

    drivingStatusChanged() {
        let apiUser = this.api.getUser();
        let activeDriver = this.userService.getDriversSeatUser();
        let isActiveDriver = apiUser && apiUser.name && activeDriver && sanitizeUserName(apiUser.name) === sanitizeUserName(activeDriver.name);
        let currentVehicle = this.getCurrentVehicle();

        // This flag is controlled by nativeContainer as well as developer options
        if (window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_AUTO_DRIVE_ACTIONS) {
            return;
        }

        if (!(apiUser && apiUser.name) || (this.userService.isDriverUsingUI(apiUser.name) && !isActiveDriver) || !currentVehicle) {
            dispatcher.trigger(DispatcherEvents.LockscreenToggle, false);
            return;
        }

        // Don't change lockscreen status if we don't know what the driving status is
        if (this.store.getState().data.driving === undefined) {
            return;
        }

        if (this.store.getState().data.driving === true) {
            dispatcher.trigger(DispatcherEvents.LockscreenToggle, activeDriver && sanitizeUserName(activeDriver.name));
            return;
        }

        dispatcher.trigger(DispatcherEvents.LockscreenToggle, false);
    }

    checkCustomCodeAndRulesets(loginPageLock: boolean): Promise<boolean> {
        return new Promise((resolve, reject) => {
            // Bypass validation to clear lockscreen 'driving' dialog. Custom codes from prev db retained to login screen
            if (!loginPageLock && this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_DRIVE_LOCK_MOTION_DETECTION)) {
                resolve(true);
                return;
            }

            // Enforce lockscreen validation regardless of HOS ruleset
            if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_DRIVE_LOCK_MOTION_DETECTION_FOR_ALL_RULESETS)) {
                reject(false);
                return;
            }

            if (this.getUser() && !isInCanada(this.hosProcess.getLatestHosRuleSetIdShortened())) {
                resolve(true);
                return;
            }

            reject(false);
            return;
        });
    }

    checkIox(): Promise<boolean> {
        const ioxAttached = this.store.getState().mobile.ioxAttached ?? false;
        const gpsData = this.gpsData;
        const currentUser = this.getUser();

        return new Promise((resolve, reject) => {
            if (ioxAttached && gpsData && gpsData.source === "Iox" && gpsData.speed !== null) {
                const speedKmh = gpsData.speed;
                log(Source.USER_EVENT, `LockScreen: Speed=${ speedKmh } Source=${ SpeedSource[SpeedSource.Iox] } Cached=false`, currentUser);
                resolve(this.isStopped(speedKmh));
                return;
            }
            reject(false);
        });
    }

    /**
     * The `mobile.lastKnownSpeed` in store is set by the `DeviceStatusInfo` or `StatusData` validation in this class.
     * @returns Promise reject with false to continue to next validation step, or resolve with the decision on speed
     */
    checkStore(): Promise<boolean> {
        const currentUser = this.getUser();
        if (!currentUser) {
            return Promise.reject(false);
        }

        const now = getServerDate();
        const tenSecondsAgo = shiftDateTime(now, -10 * SECONDS);
        const lastKnownSpeed = this.store.getState().mobile.lastKnownSpeed;

        return new Promise((resolve, reject) => {
            if (lastKnownSpeed && lastKnownSpeed?.date >= tenSecondsAgo) {
                log(
                    Source.USER_EVENT,
                    `LockScreen: Speed=${ lastKnownSpeed.speed } Source=${ SpeedSource[lastKnownSpeed.source] } Cached=true Date=${ JSON.stringify(lastKnownSpeed.date) }`,
                    currentUser
                );
                // lastKnownSpeed is in km/h, so no conversion needed
                resolve(this.isStopped(lastKnownSpeed.speed));
                return;
            }

            reject(false);
            return;
        });
    }

    checkStatusData(): Promise<boolean> {
        const currentUser = this.getUser();
        const currentVehicle = this.getCurrentVehicle() as IIdEntity;
        if (!currentUser || !currentVehicle) {
            return Promise.reject(false);
        }

        let now = getServerDate();
        let twoMinutesAgo = shiftDateTime(now, -2 * MINUTES);
        return new Promise((resolve, reject) => {
            return getStatusData(this.api, currentVehicle.id, twoMinutesAgo, now, "DiagnosticEngineRoadSpeedId", 50)
                .then(sd => {
                    if (!sd || sd.length === 0) {
                        log(Source.USER_EVENT, "LockScreen: checkStatusData no returned values", currentUser);
                        reject(false);
                        return;
                    }

                    let lastStatusData: IStatusData = sd.sort(dateTimeComparator(false))[0];

                    // Update valid dates after response & if latest one is older than a minute - reject
                    now = getServerDate();
                    twoMinutesAgo = shiftDateTime(now, -2 * MINUTES);
                    if (lastStatusData.dateTime > now.toISOString() || lastStatusData.dateTime < twoMinutesAgo.toISOString()) {
                        log(
                            Source.USER_EVENT,
                            `LockScreen: Invalid date Now=${ now.toISOString() } TwoMinutesAgo=${ twoMinutesAgo.toISOString() } StatusData=${ lastStatusData.dateTime }`,
                            currentUser
                        );
                        reject(false);
                        return;
                    }

                    if (lastStatusData && !isNaN(lastStatusData.data)) {
                        this.store.dispatch(MobileActions.setLastKnownSpeed({
                            speed: lastStatusData.data,
                            source: SpeedSource.StatusData,
                            date: getServerDate()
                        }));
                        log(
                            Source.USER_EVENT,
                            `LockScreen: Speed=${ lastStatusData.data } Source=${ SpeedSource[SpeedSource.StatusData] } Cached=false Datetime=${ lastStatusData.dateTime }`,
                            currentUser
                        );
                        // StatusData speed is in km/h, so no conversion needed
                        resolve(this.isStopped(lastStatusData.data));
                        return;
                    }

                    reject(false);
                    return;
                })
                .catch(e => {
                    error(Source.USER_EVENT, "checkStatusData: Failed to get StatusData");
                    reject(e);
                    return;
                });
        });
    }

    checkDeviceStatusInfo(): Promise<boolean> {
        const currentUser = this.getUser();
        const currentVehicle = this.getCurrentVehicle() as IIdEntity;
        if (!currentUser || !currentVehicle) {
            return Promise.reject(false);
        }

        return new Promise((resolve, reject) => {
            return getDeviceStatusInfo(this.api, currentVehicle.id)
                .then(dsi => {
                    const twoMinutesAgo = shiftDateTime(getServerDate(), -2 * MINUTES);
                    if (dsi && dsi.length === 1 && dsi[0] && !isNaN(dsi[0].speed) && new Date(dsi[0].dateTime) > twoMinutesAgo) {
                        this.store.dispatch(MobileActions.setLastKnownSpeed({
                            speed: dsi[0].speed,
                            source: SpeedSource.DeviceStatusInfo,
                            date: getServerDate()
                        }));
                        log(
                            Source.USER_EVENT,
                            `LockScreen: Speed=${ dsi[0].speed } Source=${ SpeedSource[SpeedSource.DeviceStatusInfo] } Cached=false DateTime=${ dsi[0].dateTime }`,
                            currentUser
                        );
                        // DeviceStatusInfo speed is in km/h, so no conversion needed
                        resolve(this.isStopped(dsi[0].speed));
                        return;
                    }

                    reject(false);
                    return;
                })
                .catch(e => {
                    error(Source.USER_EVENT, "checkDeviceStatusInfo: Failed to get DeviceStatusInfo");
                    reject(e);
                    return;
                });
        });
    }

    checkPhoneGps(): Promise<boolean> {
        const maximumAccuracyDistance = 1000;
        const maxAgeMs = 10000;
        const currentUser = this.getUser();

        return new Promise((resolve, reject) => {
            return getLastPosition(this.store, {}, maxAgeMs)
                .then(position => {
                    if (position && position.coords && position.coords.speed !== null && position.coords.accuracy <= maximumAccuracyDistance) {
                        const speed = position.coords.speed * 3.6;
                        log(
                            Source.USER_EVENT,
                            `LockScreen: Speed=${ speed } Source=${ SpeedSource[SpeedSource.PhoneGps] } Cached=false DateTime=${ new Date(position.timestamp).toISOString() }`,
                            currentUser
                        );
                        resolve(this.isStopped(speed));
                        return;
                    }

                    reject(false);
                    return;
                })
                .catch(e => {
                    error(Source.USER_EVENT, "checkPhoneGps: Failed to get location");
                    reject(e);
                    return;
                });
        });
    }

    canClearLockScreen(loginPageLock: boolean): Promise<boolean> {
        return new Promise((resolve, _) => {
            resolve(
                this.checkCustomCodeAndRulesets(loginPageLock)
                    .then((res) => {
                        return res;
                    })
                    .catch(() => {
                        return this.checkIox();
                    })
                    .catch(() => {
                        return this.checkStore();
                    })
                    .catch(() => {
                        return this.checkStatusData();
                    })
                    .catch(() => {
                        return this.checkDeviceStatusInfo();
                    })
                    .catch(() => {
                        return this.checkPhoneGps();
                    })
                    .catch(() => {
                        const currentUser = this.getUser();
                        log(Source.USER_EVENT, "LockScreen: Could not determine speed.", currentUser);
                        return true;
                    })
            );
        }).then((clearLockScreenResult: boolean) => {
            return new Promise((resolve, _) => {
                let currentVehicle = this.getCurrentVehicle() as IIdEntity;
                let currentUser = this.getUser();

                if (!currentVehicle || clearLockScreenResult) {
                    resolve(clearLockScreenResult);
                    return;
                }

                // If blocked from clearing lock screen, ensure the user is within 25km of the device
                return this.vehicleProximityService.getDeviceStatus(currentVehicle.id).then(status => {
                    log(
                        Source.USER_EVENT,
                        `LockScreen: Distance between Vehicle (via DeviceStatusInfo) and User's Device (via Geolocation): ${ status.distance }km`,
                        currentUser
                    );

                    if (status.distance > constants.LOCKSCREEN_PROXIMITY_RADIUS) {
                        resolve(true);
                        return;
                    } else {
                        resolve(clearLockScreenResult);
                        return;
                    }
                }).catch(err => {
                    error(Source.USER_EVENT, `LockScreen: Error comparing DeviceStatusInfo to Geolocation: ${ err }`, currentUser);
                    resolve(clearLockScreenResult);
                });
            });
        });
    }

    /**
     * Validate if measured speed is below the stopping threshold.
     * @param speedKmh - speed in km/h
     * @returns true if speed is below the stopping threshold
     */
    isStopped(speedKmh: number) {
        return speedKmh <= (this.featureFlagsService.getFeatureFlagValue(FeatureFlagName.LOCKSCREEN_STOPPING_THRESHOLD, constants.MOVEMENT.STOPPING_THRESHOLD) as number);
    }

    private storeUnsubscribe = () => {};

    private getUser(): User | null {
        let currentUser = new User(this.userService.getCurrentUser());
        if (currentUser && currentUser.id && currentUser.id !== NOUSERID) {
            return currentUser;
        }
        return null;
    }

    private getCurrentVehicle() {
        let currentVehicle = getCurrentVehicle() as IIdEntity;
        if (currentVehicle && currentVehicle.id && currentVehicle.id !== NODEVICEID) {
            return currentVehicle;
        }
        return null;
    }
}